import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import config from '../api/config';

const instance: AxiosInstance = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let isRefreshing = false;
let isLoggedOut = false;
let failedQueue: {
  resolve: (value?: AxiosResponse<any>) => void;
  reject: (error: AxiosError) => void;
}[] = [];

const processQueue = (error: AxiosError | null, response?: AxiosResponse) => {
  failedQueue.forEach((prom) => {
    error ? prom.reject(error) : prom.resolve(response);
  });
  failedQueue = [];
};

// local refresh to avoid circular import and interceptor recursion
async function refreshTokenInternal(): Promise<void> {
  await instance.post('/Auth/refresh', {});
}

instance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<any> => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (isLoggedOut || !error.response) {
      return Promise.reject(error);
    }

    const isUnauthorized = error.response.status === 401;
    const isRetryable = !originalRequest._retry;
    const isNotRefreshCall = !originalRequest.url?.includes('/Auth/refresh');
    const isNotLoginCall = !originalRequest.url?.includes('/Auth/connect');

    if (isUnauthorized && isRetryable && isNotRefreshCall && isNotLoginCall) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: () => resolve(instance(originalRequest)),
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        await refreshTokenInternal();
        processQueue(null);
        return instance(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError);
        isLoggedOut = true;
        document.cookie = 'access_token=; Max-Age=0; path=/;';
        document.cookie = 'refresh_token=; Max-Age=0; path=/;';
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
